import React from 'react';
import { Skeleton } from '@components/atoms/Skeleton';
import { useTranslation } from '@hooks/useTranslation';

/**
 * Sprint 34, story-176. Presentation-only — extracted from the inline `<p>` in `MaterialsSubTab`
 * per `docs/ux/story-176-design.md`. Does not fetch, does not know about
 * `warehouseMaterialsCount`/`manualMaterialsCount` individually, and does not decide whether to
 * render at all — the parent computes `itemCount` and decides "omit when zero items and not
 * loading/error".
 */
interface MaterialSummaryLineProps {
  totalCost: number;
  totalPrice: number;
  itemCount: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const MaterialSummaryLine: React.FC<MaterialSummaryLineProps> = ({
  totalCost,
  totalPrice,
  itemCount,
  isLoading,
  isError,
  onRetry,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  // Fixed min-height across loading/error/success so the material rows below never jump.
  return (
    <div className="mt-0.5 min-h-[18px]">
      {isLoading ? (
        <Skeleton variant="shimmer" width={180} height={14} rounded="sm" />
      ) : isError ? (
        <p className="text-xs text-red-600">
          {tTaskDetail.materials.summaryLoadError}{' '}
          <button type="button" onClick={onRetry} className="underline hover:no-underline">
            {tTaskDetail.materials.summaryRetry}
          </button>
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          {tTaskDetail.materials.itemsCostSummary
            .replace('{n}', String(itemCount))
            .replace('{cost}', formatCurrency(totalCost))}
          {totalPrice > 0 && tTaskDetail.materials.clientSuffix.replace('{price}', formatCurrency(totalPrice))}
        </p>
      )}
    </div>
  );
};
